import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  OpenTrowserOptions,
  TrowserContext,
  TrowserContextValue,
  TrowserDataByKey,
} from './trowser.context';
import {
  parseStack,
  serializeStack,
  TROWSER_PARAM,
  TROWSER_SECTION_PARAM,
  TrowserData,
  TrowserKey,
} from './trowser.types';

// History-state key marking an entry we pushed to open a trowser, so close can
// pop it (browser-Back-equivalent) instead of leaving an orphan same-URL entry.
const OPENED_BY_PUSH_STATE_KEY = '__trowserOpenedByPush';

/**
 * Owns URL-backed open state for all trowsers; mount once inside the router.
 * Open state lives in the `modalOpen` (+ `section`) params so trowsers
 * deep-link, survive reload, and close on browser Back. Opening pushes a
 * history entry; closing and section changes replace. Transient `modalData`
 * lives in memory here.
 */
export function TrowserProvider({ children }: PropsWithChildren) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [modalDataByKey, setModalDataByKey] = useState<TrowserDataByKey>({});

  // Whether the current history entry is one we pushed to open a trowser (vs. a
  // deep-linked/reloaded open, where `modalOpen` arrived on the entry itself).
  // Stored per-entry in history state — not a session counter — so that a
  // browser Back, which closes the trowser without calling closeTrowser, simply
  // moves off the flagged entry rather than leaving a stale count behind.
  const isPushedOpenEntry = Boolean(
    (location.state as { [OPENED_BY_PUSH_STATE_KEY]?: boolean } | null)?.[
      OPENED_BY_PUSH_STATE_KEY
    ]
  );
  // Read via a ref so open/close/setSection stay stable callbacks (depending on
  // `location`/`searchParams` would churn `open`, breaking openOnMount effects).
  const historyStateRef = useRef<{
    isPushedOpenEntry: boolean;
    state: unknown;
  }>({ isPushedOpenEntry, state: location.state });
  historyStateRef.current = { isPushedOpenEntry, state: location.state };

  const openTrowsers = useMemo(
    () => parseStack(searchParams.get(TROWSER_PARAM)),
    [searchParams]
  );
  const section = searchParams.get(TROWSER_SECTION_PARAM);

  // Latest params read via a ref so `openTrowser` can decide push-vs-replace
  // without listing `searchParams` as a dep — depending on it would change
  // `openTrowser`'s identity on every navigation and churn the `open` callbacks
  // that hosts pass to effects.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // TS can't verify a generic index-write into a per-key-typed map, so the
  // target is cast here — the only cast; reads stay typed.
  const writeModalData = useCallback(
    <K extends TrowserKey>(key: K, data: TrowserData<K> | undefined) => {
      setModalDataByKey((prev) => {
        const next = { ...prev };
        (next as Record<TrowserKey, unknown>)[key] = data;
        return next;
      });
    },
    []
  );

  const setModalData = useCallback(
    <K extends TrowserKey>(key: K, data: TrowserData<K>) =>
      writeModalData(key, data),
    [writeModalData]
  );

  const openTrowser = useCallback(
    <K extends TrowserKey>(key: K, options?: OpenTrowserOptions<K>) => {
      writeModalData(key, options?.data);

      // setSearchParams picks push vs replace once (not per-update), so decide
      // here from the latest params.
      const current = searchParamsRef.current;
      const nextStack = parseStack(current.get(TROWSER_PARAM));
      const existing = nextStack.findIndex((trowser) => trowser.key === key);
      const entry = { key, target: options?.target };
      if (existing === -1) {
        nextStack.push(entry);
      } else {
        nextStack[existing] = entry;
      }

      setSearchParams(
        (prev) => {
          prev.set(TROWSER_PARAM, serializeStack(nextStack));
          if (options?.section) {
            prev.set(TROWSER_SECTION_PARAM, options.section);
          } else {
            prev.delete(TROWSER_SECTION_PARAM);
          }
          return prev;
        },
        // Opening pushes a history entry (flagged so close can pop it) so
        // browser Back closes the trowser. Re-opening an already-open trowser
        // replaces its target or section and preserves the entry's flag. This
        // prevents save flows from leaving an obsolete draft target in history.
        existing !== -1
          ? { replace: true, state: historyStateRef.current.state }
          : {
              state: {
                ...(historyStateRef.current.state as object | null),
                [OPENED_BY_PUSH_STATE_KEY]: true,
              },
            }
      );
    },
    [setSearchParams, writeModalData]
  );

  const closeTrowser = useCallback(
    (key?: TrowserKey) => {
      const topKey = openTrowsers[openTrowsers.length - 1]?.key;
      const removeKey = key ?? topKey;
      if (!removeKey) return;

      setModalDataByKey((prev) => {
        if (!(removeKey in prev)) return prev;
        const next = { ...prev };
        delete next[removeKey];
        return next;
      });

      // If we're closing the topmost trowser and the current history entry is
      // one we pushed to open it, pop that entry (like browser Back) so no
      // orphan same-URL entry is left behind. Callers bypass the unsaved-changes
      // blocker before this (see useTrowser), so the POP won't re-prompt.
      if (removeKey === topKey && historyStateRef.current.isPushedOpenEntry) {
        navigate(-1);
        return;
      }

      // Otherwise (deep-linked/reloaded open, or closing a non-topmost trowser)
      // there's no pushed entry to pop, so strip the params in place.
      setSearchParams(
        (prev) => {
          const next = parseStack(prev.get(TROWSER_PARAM)).filter(
            (trowser) => trowser.key !== removeKey
          );
          if (next.length) {
            prev.set(TROWSER_PARAM, serializeStack(next));
          } else {
            prev.delete(TROWSER_PARAM);
          }
          // Section belonged to the closing topmost trowser; drop it.
          prev.delete(TROWSER_SECTION_PARAM);
          return prev;
        },
        { replace: true }
      );
    },
    [navigate, openTrowsers, setSearchParams]
  );

  const setSection = useCallback(
    (nextSection: string | null) => {
      setSearchParams(
        (prev) => {
          if (nextSection) {
            prev.set(TROWSER_SECTION_PARAM, nextSection);
          } else {
            prev.delete(TROWSER_SECTION_PARAM);
          }
          return prev;
        },
        // Replace: switching sections shouldn't stack history entries. Preserve
        // the entry's state so an open-by-push entry keeps its flag (else close
        // couldn't tell it should pop rather than strip).
        { replace: true, state: historyStateRef.current.state }
      );
    },
    [setSearchParams]
  );

  const value = useMemo<TrowserContextValue>(
    () => ({
      openTrowsers,
      section,
      modalDataByKey,
      openTrowser,
      closeTrowser,
      setSection,
      setModalData,
    }),
    [
      openTrowsers,
      section,
      modalDataByKey,
      openTrowser,
      closeTrowser,
      setSection,
      setModalData,
    ]
  );

  return (
    <TrowserContext.Provider value={value}>{children}</TrowserContext.Provider>
  );
}
